// src/pages/LMS/Invitations/CourseInvitations.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent,
  TextField, Dialog, Divider, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Chip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Menu, LinearProgress, Alert, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as AnalyticsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  ArrowBack as BackIcon,
  Replay as ResendIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const CourseInvitations = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [createDialog, setCreateDialog] = useState(false);
  const [analyticsDialog, setAnalyticsDialog] = useState({ open: false, invitation: null });
  const [resendDialog, setResendDialog] = useState({ open: false, invitation: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);

  const [form, setForm] = useState({
    course_id: '',
    title: '',
    message_body: '',
    channel: 'email',
    send_email: true,
    send_sms: false,
    due_date: '',
    reminder_days: '7,3,1',
    auto_enroll: true,
    recipient_emails: '',
    recipient_student_ids: []
  });

  const [resendRecipients, setResendRecipients] = useState([]);

  useEffect(() => {
    loadInvitations();
    loadCourses();
    loadStudents();
  }, []);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/lms/invitations');
      setInvitations(response.data.data);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axiosInstance.get('/api/lms/courses', {
        params: { status: 'published', per_page: 100 }
      });
      setCourses(response.data.data);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await axiosInstance.get('/api/lms/students', {
        params: { status: 'active', per_page: 100 }
      });
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const handleCreateInvitation = async () => {
    if (!form.course_id || !form.title) {
      alert('Course and title are required');
      return;
    }

    try {
      // Prepare recipient list
      const recipients = [];
      
      // Add from email list
      if (form.recipient_emails) {
        const emails = form.recipient_emails.split(',').map(e => e.trim()).filter(Boolean);
        emails.forEach(email => {
          recipients.push({ email, student_id: null });
        });
      }
      
      // Add from selected students
      form.recipient_student_ids.forEach(studentId => {
        const student = students.find(s => s.id === studentId);
        if (student) {
          recipients.push({ email: student.email, student_id: studentId });
        }
      });

      const payload = {
        ...form,
        recipients,
        reminder_days: form.reminder_days.split(',').map(d => parseInt(d.trim())).filter(Boolean)
      };

      await axiosInstance.post('/api/lms/invitations', payload);
      alert('Invitation created successfully');
      setCreateDialog(false);
      resetForm();
      loadInvitations();
    } catch (error) {
      console.error('Error creating invitation:', error);
      alert('Error creating invitation');
    }
  };

  const handleSendInvitation = async (invitationId) => {
    if (!window.confirm('Send this invitation now?')) return;

    try {
      await axiosInstance.post(`/api/lms/invitations/${invitationId}/send`);
      alert('Invitation sent successfully');
      loadInvitations();
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Error sending invitation');
    }
  };

  const handleResend = async () => {
    if (resendRecipients.length === 0) {
      alert('Please select recipients');
      return;
    }

    try {
      await axiosInstance.post(
        `/api/lms/invitations/${resendDialog.invitation.id}/resend`,
        { recipient_ids: resendRecipients }
      );
      alert('Invitation resent successfully');
      setResendDialog({ open: false, invitation: null });
      setResendRecipients([]);
      loadInvitations();
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Error resending invitation');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Delete this invitation?')) return;

    try {
      await axiosInstance.delete(`/api/lms/invitations/${invitationId}`);
      alert('Invitation deleted successfully');
      loadInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
    }
  };

  const resetForm = () => {
    setForm({
      course_id: '',
      title: '',
      message_body: '',
      channel: 'email',
      send_email: true,
      send_sms: false,
      due_date: '',
      reminder_days: '7,3,1',
      auto_enroll: true,
      recipient_emails: '',
      recipient_student_ids: []
    });
  };

  const getStatusChip = (status) => {
    const colors = {
      draft: 'default',
      sent: 'info',
      scheduled: 'warning',
      completed: 'success'
    };
    return <Chip label={status.toUpperCase()} color={colors[status]} size="small" />;
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              <SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Course Invitations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Send email and SMS invitations to enroll students
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          Create Invitation
        </Button>
      </Box>

      {/* Invitations Grid */}
      <Grid container spacing={3}>
        {invitations.map((invitation) => (
          <Grid item xs={12} sm={6} md={4} key={invitation.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  {getStatusChip(invitation.status)}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setMenuAnchor(e.currentTarget);
                      setSelectedInvitation(invitation);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Typography variant="h6" gutterBottom>
                  {invitation.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {invitation.course?.title}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  {invitation.send_email && (
                    <Chip icon={<EmailIcon />} label="Email" size="small" />
                  )}
                  {invitation.send_sms && (
                    <Chip icon={<SmsIcon />} label="SMS" size="small" />
                  )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Recipients
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {invitation.total_recipients}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Sent
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {invitation.sent_count || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Clicked
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {invitation.clicked_count || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Enrolled
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {invitation.enrolled_count || 0}
                    </Typography>
                  </Grid>
                </Grid>

                {invitation.total_recipients > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Conversion Rate: {
                        ((invitation.enrolled_count / invitation.total_recipients) * 100).toFixed(1)
                      }%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(invitation.enrolled_count / invitation.total_recipients) * 100}
                      sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {selectedInvitation?.status === 'draft' && (
          <MenuItem onClick={() => {
            setMenuAnchor(null);
            handleSendInvitation(selectedInvitation.id);
          }}>
            <SendIcon sx={{ mr: 1 }} fontSize="small" />
            Send Now
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          setAnalyticsDialog({ open: true, invitation: selectedInvitation });
        }}>
          <AnalyticsIcon sx={{ mr: 1 }} fontSize="small" />
          View Analytics
        </MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          setResendDialog({ open: true, invitation: selectedInvitation });
        }}>
          <ResendIcon sx={{ mr: 1 }} fontSize="small" />
          Resend
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            handleDeleteInvitation(selectedInvitation?.id);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Invitation Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Course Invitation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Course</InputLabel>
              <Select
                value={form.course_id}
                onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                label="Course"
              >
                {courses.map(course => (
                  <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Campaign Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message Body"
              value={form.message_body}
              onChange={(e) => setForm({ ...form, message_body: e.target.value })}
              placeholder="Write your invitation message here..."
            />

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.send_email}
                    onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                  />
                }
                label="Send Email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.send_sms}
                    onChange={(e) => setForm({ ...form, send_sms: e.target.checked })}
                  />
                }
                label="Send SMS"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.auto_enroll}
                    onChange={(e) => setForm({ ...form, auto_enroll: e.target.checked })}
                  />
                }
                label="Auto-Enroll on Click"
              />
            </Stack>

            <TextField
              fullWidth
              type="date"
              label="Due Date (Optional)"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Reminder Days (comma-separated)"
              value={form.reminder_days}
              onChange={(e) => setForm({ ...form, reminder_days: e.target.value })}
              placeholder="7,3,1"
              helperText="Days before due date to send reminders"
            />

            <Typography variant="subtitle2" gutterBottom>
              Recipients
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Email Addresses (comma-separated)"
              value={form.recipient_emails}
              onChange={(e) => setForm({ ...form, recipient_emails: e.target.value })}
              placeholder="email1@example.com, email2@example.com"
            />

            <FormControl fullWidth>
              <InputLabel>Select Students</InputLabel>
              <Select
                multiple
                value={form.recipient_student_ids}
                onChange={(e) => setForm({ ...form, recipient_student_ids: e.target.value })}
                label="Select Students"
                renderValue={(selected) => `${selected.length} students selected`}
              >
                {students.map(student => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.first_name} {student.last_name} ({student.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvitation}>
            Create & Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog
        open={analyticsDialog.open}
        onClose={() => setAnalyticsDialog({ open: false, invitation: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Invitation Analytics</DialogTitle>
        <DialogContent>
          {analyticsDialog.invitation && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {analyticsDialog.invitation.total_recipients}
                    </Typography>
                    <Typography variant="caption">Total Recipients</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {analyticsDialog.invitation.sent_count || 0}
                    </Typography>
                    <Typography variant="caption">Sent</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {analyticsDialog.invitation.clicked_count || 0}
                    </Typography>
                    <Typography variant="caption">Clicked</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {analyticsDialog.invitation.enrolled_count || 0}
                    </Typography>
                    <Typography variant="caption">Enrolled</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Performance Metrics
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Open Rate</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {analyticsDialog.invitation.open_rate?.toFixed(1) || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analyticsDialog.invitation.open_rate || 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Click Rate</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {analyticsDialog.invitation.click_rate?.toFixed(1) || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analyticsDialog.invitation.click_rate || 0}
                      color="info"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Conversion Rate</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {analyticsDialog.invitation.conversion_rate?.toFixed(1) || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={analyticsDialog.invitation.conversion_rate || 0}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialog({ open: false, invitation: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Dialog */}
      <Dialog
        open={resendDialog.open}
        onClose={() => setResendDialog({ open: false, invitation: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resend Invitation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select recipients to resend the invitation to:
          </Typography>
          {/* Add recipient selection list here */}
          <Alert severity="info">
            Feature coming soon: Select specific recipients to resend
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendDialog({ open: false, invitation: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResend}>
            Resend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseInvitations;